import {
  FunctionComponent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'

import styled, { css, useTheme } from 'styled-components'

import config from '@config'
import { isMobileUserAgent } from '@measures/responsive'
import translate from '@i18n'
import useIsEndOfArticle from '@hooks/useIsEndOfArticle'
import useIsInRecipeContent from '@hooks/useIsInRecipeContent'
import useOnScroll from '@hooks/useOnScroll'
import usePageMetadata from '@hooks/usePageMetadata'
import useTracking, { TrackingFnType } from '@hooks/useTracking'
import { stripHtml } from '@hooks/useTracking/utils'
import IconButton from '@components/Buttons/IconButton'
import Popover from '@components/Popover'
import SocialButton from '@components/Social/Button'
import Copy from '@components/Social/Copy'
import Email from '@components/Social/Email'
import Facebook from '@components/Social/Facebook'
import LabelWrapper from '@components/Social/LabelWrapper'
import Twitter from '@components/Social/Twitter'
import { getUTMParameters } from '@components/Social/utils'
import Whatsapp from '@components/Social/Whatsapp'

import Print from './Print'

const {
  backend: { baseUrl, rootUrl },
} = config

const popoverContentWidth = 244

interface ShareCTAButtonProps {
  title: string
  articleId: string
}

const Wrapper = styled.div`
  position: relative;
`

const StyledPopover = styled(Popover)`
  ${({
    position,
    theme: {
      colors: { fillSurface },
    },
  }) => css`
    background-color: ${fillSurface};
    ${position === 'top' &&
    css`
      bottom: 16px;
      right: initial;
      left: -36px;

      &:after {
        left: 8px;
      }
    `}
    ${position === 'bottom' &&
    css`
      bottom: initial;
      right: initial;
      left: -36px;
      top: 18px;

      &:after {
        left: 8px;
      }
    `}
  `}
`

const PopoverContent = styled.div`
  ${({
    theme: {
      spacing: { spacing16 },
      colors: { fillSurface },
    },
  }) => css`
    width: ${popoverContentWidth}px;
    padding: ${spacing16};
    box-sizing: border-box;
    background-color: ${fillSurface};
  `}
`

const PopoverHeader = styled.div`
  ${({
    theme: {
      spacing: { spacing16 },
    },
  }) => css`
    display: flex;
    justify-content: space-between;
    margin-bottom: ${spacing16};
  `}
`

const PopoverTitle = styled.div`
  ${({
    theme: {
      typography: {
        headings: {
          xxsmall: { bundledCSS: xxsmallHeadingsCSS },
        },
      },
      colors: { textDefault },
    },
  }) => css`
    ${xxsmallHeadingsCSS};
    color: ${textDefault};
  `}
`

const PopoverBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const ShareCTAButton: FunctionComponent<ShareCTAButtonProps> = ({ title }) => {
  const theme = useTheme()
  const { url } = usePageMetadata()
  const isInRecipe = useIsInRecipeContent()
  const endOfArticle = useIsEndOfArticle()
  const windowRef = useRef<typeof window | null>(null)
  const [shouldTrackOnScroll, setShouldTrackOnScroll] = useState(false)
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const [isPopoverOpen, setIsPopoverOpen] = useState<boolean>(false)
  const [tooltipPosition, setTooltipPosition] = useState<'top' | 'bottom'>(
    'bottom',
  )
  const shareUrl = `${baseUrl}${rootUrl}${url?.slice(1)}`

  const togglePopover = useCallback(() => {
    setIsPopoverOpen((prevValue) => !prevValue)
  }, [])

  const trackingOnClick = useCallback<TrackingFnType>(
    () => ({
      event: 'element_click',
      element: `article_share_${endOfArticle ? 'bottom' : 'top'}`,
    }),
    [endOfArticle],
  )

  const trackingOnClickLegacy = useCallback<TrackingFnType>(
    ({ pageMetadata }) => ({
      event: 'article_share',
      eventCategory: 'article',
      eventAction: `share_${endOfArticle ? 'bottom' : 'top'}`,
      eventLabel: `${pageMetadata.id}:${stripHtml(pageMetadata.title)}`,
    }),
    [endOfArticle],
  )

  const trackingFunction = useTracking(trackingOnClick)
  const trackingFunctionLegacy = useTracking(trackingOnClickLegacy)

  const onShareButtonClick = useCallback(async () => {
    if (navigator.share && isMobileUserAgent()) {
      try {
        const utmSuffix = getUTMParameters({
          utmSource: 'native-share',
          fullSuffix: true,
        })

        await navigator.share({
          title,
          text: `${translate('shareMessage')}\n\n${title}\n\n`,
          url: `${shareUrl}${utmSuffix}`,
        })
        trackingFunction()
        // The *Legacy tracking functions will be deleted when the tracking team is ready to switch to the new events
        trackingFunctionLegacy()
      } catch {
        // navigator.share raises an error if the user cancels the share
        // Do nothing in this case
      }
    } else {
      togglePopover()
    }
  }, [shareUrl, title, togglePopover, trackingFunction, trackingFunctionLegacy])

  const handleScroll = useCallback(() => {
    const percentage = 55
    if (wrapperRef.current && tooltipPosition) {
      const { top } = wrapperRef.current.getBoundingClientRect()
      const windowHeight = window.innerHeight
      const calculatedItemPosition = windowHeight - Math.floor(top)
      const reachedPercentage = Math.floor(
        (calculatedItemPosition / windowHeight) * 100,
      )
      if (
        reachedPercentage < percentage &&
        tooltipPosition === 'bottom' &&
        calculatedItemPosition < 300
      ) {
        setTooltipPosition('top')
      } else if (reachedPercentage > percentage && tooltipPosition === 'top') {
        setTooltipPosition('bottom')
      }
    }
  }, [tooltipPosition])

  useOnScroll({
    track: shouldTrackOnScroll,
    delay: 60,
    elementRef: windowRef,
    onScroll: handleScroll,
  })

  useEffect(() => {
    windowRef.current = window
    if (!shouldTrackOnScroll) {
      setShouldTrackOnScroll(true)
    }
    handleScroll()
  }, [handleScroll, shouldTrackOnScroll])

  return (
    <Wrapper ref={wrapperRef}>
      <LabelWrapper labelText={translate('article.sharing.share')}>
        <SocialButton
          ariaLabel="Share"
          onClick={onShareButtonClick}
          type="share"
          isActive={isPopoverOpen}
          buttonVariant="colored"
        />
      </LabelWrapper>

      {isPopoverOpen && (
        <StyledPopover
          position={isInRecipe ? 'top' : tooltipPosition}
          trackClickOutside={true}
          wrapperRef={wrapperRef}
          onClickOutside={togglePopover}
        >
          <PopoverContent>
            <PopoverHeader>
              <PopoverTitle>{translate('social.share')}</PopoverTitle>
              <IconButton
                ariaLabel="Close"
                buttonSize={16}
                onClick={togglePopover}
                iconName="xmark-large"
                iconSize={16}
                iconColor={theme.colors.iconDefault}
              />
            </PopoverHeader>
            <PopoverBody>
              <Facebook shareUrl={shareUrl} label="Facebook" />
              <Twitter shareUrl={shareUrl} shareTitle={title} label="X" />
              <Whatsapp
                shareUrl={shareUrl}
                shareTitle={title}
                label="WhatsApp"
              />
              <Email shareUrl={shareUrl} shareTitle={title} label="E-Mail" />
              <Copy shareUrl={shareUrl} label={translate('social.copy')} />
              {isInRecipe && <Print label="Print" />}
            </PopoverBody>
          </PopoverContent>
        </StyledPopover>
      )}
    </Wrapper>
  )
}

export default ShareCTAButton
